import { useEffect, useMemo, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { adminApi } from '../../utils/api';
import { Alert, Badge, Spinner } from './shared';

function fmtNumber(n) {
    return Number(n || 0).toLocaleString('vi-VN');
}

function fmtRelative(iso) {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        const diffMs = Date.now() - d.getTime();
        const min = Math.floor(diffMs / 60000);
        if (min < 1) return 'vừa xong';
        if (min < 60) return `${min} phút trước`;
        const hr = Math.floor(min / 60);
        if (hr < 24) return `${hr} giờ trước`;
        const day = Math.floor(hr / 24);
        if (day < 7) return `${day} ngày trước`;
        return d.toLocaleDateString('vi-VN');
    } catch {
        return '—';
    }
}

function MetricCard({ label, value, hint }) {
    return (
        <div className="border border-gray-200 rounded-lg bg-white p-4">
            <div className="text-[11px] text-gray-500 uppercase tracking-wide">{label}</div>
            <div className="text-2xl font-bold text-gray-900 mt-1.5 tabular-nums">{fmtNumber(value)}</div>
            {hint && <div className="text-[11px] text-gray-400 mt-1">{hint}</div>}
        </div>
    );
}

function padDailyWindow(daily, days = 30) {
    const byDay = new Map((daily || []).map((d) => [d.day, d.count]));
    const out = [];
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    for (let i = days - 1; i >= 0; i--) {
        const d = new Date(today);
        d.setUTCDate(today.getUTCDate() - i);
        const iso = d.toISOString().slice(0, 10);
        out.push({ day: iso, count: byDay.get(iso) || 0 });
    }
    return out;
}

function DailyChart({ daily }) {
    const options = useMemo(() => {
        const padded = padDailyWindow(daily, 30);
        const todayIso = new Date().toISOString().slice(0, 10);
        return {
            chart: { type: 'column', height: 220, style: { fontFamily: 'inherit' }, spacingTop: 10 },
            title: { text: null },
            credits: { enabled: false },
            legend: { enabled: false },
            xAxis: {
                categories: padded.map((d) => d.day.slice(5)),
                crosshair: { width: 1, color: '#d1d5db', dashStyle: 'Dash' },
                labels: {
                    style: { fontSize: '10px', color: '#9ca3af' },
                    rotation: -45,
                    step: 3,
                },
                lineColor: '#e5e7eb',
                tickLength: 0,
            },
            yAxis: {
                title: { text: null },
                labels: { style: { fontSize: '10px', color: '#9ca3af' } },
                gridLineColor: '#f3f4f6',
                min: 0,
                allowDecimals: false,
            },
            tooltip: {
                style: { fontSize: '11px' },
                pointFormat: '<b>{point.y:,.0f}</b> lượt xem',
                headerFormat: '<span style="font-size:11px;color:#6b7280">{point.key}</span><br/>',
                formatter: function () {
                    const idx = this.point.index;
                    const day = padded[idx]?.day || '';
                    return `<span style="font-size:11px;color:#6b7280">${day}</span><br/><b>${this.y.toLocaleString('vi-VN')}</b> lượt xem`;
                },
            },
            plotOptions: {
                column: {
                    borderRadius: 2,
                    borderWidth: 0,
                    pointPadding: 0.05,
                    groupPadding: 0.1,
                    color: '#818CF8',
                    states: { hover: { color: '#4F46E5' } },
                },
            },
            series: [
                {
                    name: 'Lượt xem',
                    data: padded.map((d) => ({
                        y: d.count,
                        marker: d.day === todayIso ? { lineWidth: 1, lineColor: '#4338CA' } : undefined,
                    })),
                },
            ],
        };
    }, [daily]);

    return <HighchartsReact highcharts={Highcharts} options={options} />;
}

export default function InsightStatsPanel({ insightId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res = await adminApi.getInstrumentInsightStats(insightId, {
                    topViewersLimit: 20,
                    dailyWindowDays: 30,
                });
                if (!cancelled) setData(res);
            } catch (e) {
                if (!cancelled) setError(e?.message || 'Không tải được stats');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [insightId]);

    if (loading) return <Spinner size="md" text="Đang tải stats..." center />;
    if (error) return <Alert variant="error">{error}</Alert>;
    if (!data) return null;

    const totals = data.totals || {};
    const updated = totals.updated_at ? fmtRelative(totals.updated_at) : '—';

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-2 gap-3">
                <MetricCard
                    label="Tổng lượt xem"
                    value={totals.view_count_total}
                    hint={`Cập nhật ${updated}`}
                />
                <MetricCard
                    label="Người xem (unique)"
                    value={totals.view_count_unique}
                    hint="HyperLogLog ±0.81%"
                />
            </div>

            <div className="border border-gray-200 rounded-lg bg-white p-4">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-800">Lượt xem 30 ngày</h3>
                    <span className="text-[11px] text-gray-500">
                        {fmtNumber(data.daily?.reduce((s, d) => s + d.count, 0) || 0)} sự kiện
                    </span>
                </div>
                <DailyChart daily={data.daily || []} />
            </div>

            <div className="border border-gray-200 rounded-lg bg-white p-4">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-800">Top viewers</h3>
                    <span className="text-[11px] text-gray-500">
                        {fmtNumber(data.anonymous_views || 0)} lượt anonymous
                    </span>
                </div>
                {data.top_viewers?.length ? (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50/60">
                                <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">User ID</th>
                                <th className="text-right px-3 py-2 text-xs font-medium text-gray-500 w-24">Lượt xem</th>
                                <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Lần cuối</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {data.top_viewers.map((v) => (
                                <tr key={v.user_id} className="hover:bg-gray-50">
                                    <td className="px-3 py-2 font-mono text-xs">{v.user_id}</td>
                                    <td className="px-3 py-2 text-right tabular-nums font-mono text-xs">
                                        <Badge variant="outline">{fmtNumber(v.view_count)}</Badge>
                                    </td>
                                    <td className="px-3 py-2 text-xs text-gray-500">
                                        {fmtRelative(v.last_viewed_at)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <div className="text-sm text-gray-400 italic">Chưa có user xác thực nào xem bài này.</div>
                )}
            </div>
        </div>
    );
}
